import { useCallback, useEffect, useRef, useState } from 'react';
import { mdiArrowLeft } from '@mdi/js';
import {
  Box,
  Text,
  Button,
  Heading,
  Skeleton,
  HStack,
  Flex,
  Alert,
  AlertDescription,
  AlertIcon,
  useToast,
} from '@chakra-ui/react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import {
  filterRolesForOrganization,
  formatMemberName,
  selectAccessGlobalDropdownOptions,
} from './utils/memberDetailsUtils';
import {
  getMemberDetailsErrorMessage,
  MEMBER_DETAILS_ERROR_MESSAGES,
} from './utils/memberDetailsErrors';
import { MemberDetailsHeader } from './components/MemberDetailsHeader';
import {
  ESelectAccessOptions,
  ISelectOption,
} from './utils/memberDetailsModel';
import { MemberDetailsProductAccess } from './components/MemberDetailsProductAccess';
import { MemberRemoveConfirmation } from './components/MemberRemoveConfirmation';
import { MemberTeamsSection } from './components/MemberTeamsSection';
import OrganizationAccessCard from 'features/common/components/Access/UserAccessCard';
import { useAppAccess } from 'features/common/components/OrganizationAppAccess';
import { PortalIcon } from 'features/common/components/PortalIcon';
import { Select as ReactSelect, SingleValue } from 'chakra-react-select';
import {
  useGetOrganizationMemberQuery,
  useUpdateOrganizationMemberRolesMutation,
} from 'gql/graphql';
import { flatten } from 'lodash';
import { ORG_ROLES } from 'features/common/components/OrganizationAppAccess/resource';
import { useMemberTeams } from 'features/members/hooks/useMemberTeams';
import { useDrawerStateDispatch } from 'features/main/context';
import { openDrawer } from 'features/main/context/drawer/actionCreators';
import { useFeature } from 'lib/featureFlags/features';

interface RouteParamProps {
  id: string;
  page: string;
}

export interface IMemberDetailsProps
  extends RouteComponentProps<RouteParamProps> {}

export const MemberDetails = (props: IMemberDetailsProps) => {
  const userId = props.match.params.id;
  const history = useHistory();
  const toast = useToast();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const isUserGroupsEnabled = useFeature('USER_GROUPS');

  const [updateOrganizationMemberRolesMutation] =
    useUpdateOrganizationMemberRolesMutation();

  const {
    data: memberData,
    loading: singleMemberLoading,
    error: isError,
    refetch,
  } = useGetOrganizationMemberQuery({
    variables: { id: userId },
    notifyOnNetworkStatusChange: false,
    // Keep the data of partial responses (GraphQL errors returned together with
    // data, e.g. DAM roles omitted) so the page can render what is available.
    errorPolicy: 'all',
  });

  const goBack = useCallback(
    (meta?: { from: 'member-details' }) => {
      const locationState = history.location.state as {
        returnTo?: string;
      } | null;
      const returnTo = locationState?.returnTo;
      if (returnTo) {
        history.push(returnTo, meta);
      } else {
        history.push(
          `/admin/members/page/${props.match.params.page}${history.location.search}`,
          meta,
        );
      }
    },
    [history, props.match.params.page],
  );

  // Retry loading the member after a failed GetOrganizationMember request.
  // A failed refetch rejects, but the error is surfaced through the query's
  // `error` state, so the rejection itself is intentionally swallowed.
  const retryLoadMember = useCallback(async () => {
    setIsRetrying(true);
    try {
      await refetch();
    } catch {
      // handled via the `error` returned by the query
    } finally {
      setIsRetrying(false);
    }
  }, [refetch]);

  const { hasOrgAdminOwnerAccess, hasOrgOwnerAccess } = useAppAccess();
  const { memberById } = memberData?.organization || { organization: {} };
  const { memberActions } = memberById?.appearance || {
    memberActions: undefined,
  };
  const {
    givenName,
    familyName,
    jobRole,
    email,
    phoneNumber,
    lastLogin,
    picture,
    organizationRoles,
    applications,
    marketplaceAppRoles,
  } = memberById || {};
  const applicationNodes = applications?.nodes || [];
  const marketplaceAppRolesNodes = marketplaceAppRoles?.nodes || [];
  const organizationGqlId =
    memberData?.organization?.id ||
    organizationRoles?.nodes?.[0]?.organizationId ||
    applicationNodes
      .flatMap((node) => node.assignedRoles?.nodes ?? [])
      .find((role) => role.organizationId)?.organizationId ||
    '';
  const organizationCacheKey =
    new URLSearchParams(history.location.search).get('organization') ||
    'default';
  const orgRole = organizationRoles?.nodes?.filter(
    (o) => o.organizationId === organizationGqlId,
  )[0];

  const currentOrgRole = orgRole
    ? selectAccessGlobalDropdownOptions[orgRole.name]
    : !isError
    ? selectAccessGlobalDropdownOptions['Organization User']
    : null;

  const allRoles = flatten([
    ...applicationNodes.map(
      (node) =>
        (node.assignedRoles?.nodes &&
          node.assignedRoles?.nodes?.map((n) => ({
            organizationId: n.organizationId,
            scope: n.productCode,
            tenantId: n.applicationId,
            role: n.role,
          }))) ||
        [],
    ),
    ...marketplaceAppRolesNodes.map(
      (node: {
        marketplaceAppId: string;
        organizationId: string;
        role: string;
      }) => ({
        organizationId: node.organizationId,
        scope: 'Marketplace',
        tenantId: node.marketplaceAppId,
        role: node.role,
      }),
    ),
  ]);

  const memberName = formatMemberName(givenName, familyName);
  const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
  const [selectedOrgRole, setSelectedOrgRole] = useState<ISelectOption | null>(
    null,
  );

  // We want to sync selectedOrgRole with currentOrgRole on initial load
  useEffect(() => {
    if (!singleMemberLoading && !selectedOrgRole) {
      setSelectedOrgRole(currentOrgRole);
    }
  }, [selectedOrgRole, currentOrgRole, singleMemberLoading]);

  const accessSelectOptions = hasOrgOwnerAccess
    ? Object.values(selectAccessGlobalDropdownOptions)
    : Object.values(selectAccessGlobalDropdownOptions).filter(
        (r) => r.value !== ESelectAccessOptions.owner,
      );

  /**
   * on change organization handler - updates selected role
   * @param newAccess
   * @returns void
   */
  const changeOrganizationAccess = useCallback(
    (newAccess: SingleValue<ISelectOption>) => {
      setSelectedOrgRole(newAccess);
    },
    [],
  );

  /**
   * Save organization access changes
   */
  const saveOrganizationAccess = useCallback(async () => {
    if (!orgRole || !selectedOrgRole) return;

    setIsUpdating(true);

    try {
      const { data } = await updateOrganizationMemberRolesMutation({
        variables: {
          organizationRolesToDelete: [
            {
              roleName: orgRole.name,
            },
          ],
          organizationRolesToAdd: [
            { roleName: `Organization ${selectedOrgRole.role}` },
          ],
          userId: userId,
        },
      });

      const error = data?.updateOrganizationMemberRoles.errors?.[0];
      if (error) {
        toast({
          description: error?.message,
          title: 'Error',
          status: 'error',
        });
        return;
      }

      await refetch();
    } catch (error: any) {
      const { networkError } = error ?? {};
      toast({
        description: networkError?.message || 'An error occurred',
        title: `Error ${networkError?.statusCode || ''}`,
        status: 'error',
      });
    } finally {
      setIsUpdating(false);
    }
  }, [
    orgRole,
    refetch,
    selectedOrgRole,
    toast,
    updateOrganizationMemberRolesMutation,
    userId,
  ]);

  const getOrganisationAccessReusedProps = () => ({
    formatOptionLabel: ({ role, description }: ISelectOption) => (
      <span data-testid={`option-${role}`}>
        <OrganizationAccessCard roleName={role} description={description} />
      </span>
    ),
    name: 'react-select',
  });

  const isSkeletonLoaded = !singleMemberLoading || Boolean(isError);
  const saveButtonDisabled = isUpdating || singleMemberLoading;

  const memberActionsList =
    memberActions?.nodes && memberActions?.nodes.map((node) => node.name);

  const hasRemoveAccess = memberActionsList?.includes('Remove access');
  // User can add to teams if they have remove access OR org admin/owner access
  const canManageTeams = hasRemoveAccess || hasOrgAdminOwnerAccess;

  const [, drawerDispatch] = useDrawerStateDispatch();
  const [teamsKey, setTeamsKey] = useState(0);
  const teamsRefreshTimeoutRef = useRef<number | null>(null);

  const clearTeamsRefreshTimer = useCallback(() => {
    if (!teamsRefreshTimeoutRef.current) return;
    window.clearTimeout(teamsRefreshTimeoutRef.current);
    teamsRefreshTimeoutRef.current = null;
  }, []);

  useEffect(() => {
    return () => clearTeamsRefreshTimer();
  }, [clearTeamsRefreshTimer]);

  // Fetch member's teams - hook will fetch from cache or REST API
  const {
    teams,
    isLoading: teamsLoading,
    hasResolvedTeams,
    optimisticallyAddTeams,
    optimisticallyRemoveTeam,
  } = useMemberTeams(
    userId,
    undefined,
    teamsKey,
    organizationCacheKey,
    email,
    !singleMemberLoading,
  );

  const scheduleTeamsReconcile = useCallback(() => {
    // Reconcile with server shortly after mutation success.
    // This avoids overwriting optimistic state with a briefly-stale API response.
    clearTeamsRefreshTimer();
    teamsRefreshTimeoutRef.current = window.setTimeout(() => {
      setTeamsKey((prev) => prev + 1);
      teamsRefreshTimeoutRef.current = null;
    }, 750);
  }, [clearTeamsRefreshTimer]);

  const handleTeamRemoved = useCallback(
    (teamId: string) => {
      optimisticallyRemoveTeam(teamId);
      scheduleTeamsReconcile();
    },
    [optimisticallyRemoveTeam, scheduleTeamsReconcile],
  );

  const handleTeamAdded = useCallback(
    (addedTeamIds: string[]) => {
      optimisticallyAddTeams(addedTeamIds);
      scheduleTeamsReconcile();
    },
    [optimisticallyAddTeams, scheduleTeamsReconcile],
  );

  const handleAddToTeamsClick = useCallback(() => {
    const currentTeamIds = teams.map((team) => team.id);
    drawerDispatch(
      openDrawer('add-member-to-teams-drawer', {
        memberId: userId,
        existingTeamIds: currentTeamIds,
        organizationId: organizationCacheKey,
        onSuccess: handleTeamAdded,
      }),
    );
  }, [drawerDispatch, handleTeamAdded, organizationCacheKey, teams, userId]);

  // GetOrganizationMember failed (including GraphQL errors on nested paths such
  // as organization.memberById.applications). Stay on the page, explain what
  // happened and let the user retry or navigate away themselves.
  const errorMessage = getMemberDetailsErrorMessage(isError);

  const renderErrorAlert = (
    testId: string,
    {
      withRetry = false,
      mt = '5',
      message = errorMessage,
    }: { withRetry?: boolean; mt?: string; message?: string } = {},
  ) => (
    <Alert status='warning' mt={mt} alignItems='center' data-testid={testId}>
      <AlertIcon />
      <AlertDescription flex='1'>{message}</AlertDescription>
      {withRetry && (
        <Button
          size='sm'
          variant='outline'
          ml='4'
          onClick={retryLoadMember}
          isLoading={isRetrying}
          data-testid='member-details-retry-button'
        >
          Try again
        </Button>
      )}
    </Alert>
  );

  // Nothing usable came back: show only the error state.
  if (isError && !memberById) {
    return (
      <Box data-behavior-analytics-feature='member details page'>
        <Flex w='100%' justifyContent='space-between'>
          <Button
            variant='ghost'
            alignItems='center'
            onClick={() => goBack()}
            data-testid='back-button'
            size='sm'
          >
            <PortalIcon path={mdiArrowLeft} mr='2' />
            Back to users
          </Button>
        </Flex>
        {renderErrorAlert('member-details-error', { withRetry: true })}
      </Box>
    );
  }

  return (
    <Box data-behavior-analytics-feature='member details page'>
      {/* Top Row */}
      <Flex w='100%' justifyContent='space-between'>
        <Button
          variant='ghost'
          alignItems='center'
          onClick={() => goBack()}
          data-testid='back-button'
          size='sm'
        >
          <PortalIcon path={mdiArrowLeft} mr='2' />
          Back to users
        </Button>
        <Skeleton isLoaded={isSkeletonLoaded}>
          {hasRemoveAccess && (
            <Button
              variant='outline'
              colorScheme='danger'
              onClick={() => {
                setShowRemoveModal(true);
              }}
              data-testid='remove-user-button'
              size='sm'
            >
              Remove user
            </Button>
          )}
        </Skeleton>
      </Flex>
      {/* Partial response: GraphQL errors returned together with member data */}
      {isError &&
        renderErrorAlert('member-details-partial-error', { withRetry: true })}
      {/* header */}
      <MemberDetailsHeader
        isLoading={singleMemberLoading}
        memberName={memberName}
        memberDetails={{
          jobRole,
          email,
          phoneNumber,
          picture,
        }}
        lastLogin={lastLogin}
      />
      <Flex
        gap='8'
        alignItems='flex-start'
        flexDirection={{ base: 'column', lg: 'row' }}
        mt='6'
      >
        <Box flex='1' minW='0' maxW={{ base: '100%', lg: '50%' }}>
          <Heading size='sm' mb='3' fontWeight='semibold'>
            Organization access
          </Heading>
          <Text variant='subtle' mb='4'>
            Update the organization-level role for this user.
          </Text>
          {!isSkeletonLoaded || orgRole || !isError ? (
            <HStack alignItems='flex-start'>
              <Box flex='1' minW='0' w='full' maxW={{ base: '100%', md: 'md' }}>
                <Skeleton isLoaded={isSkeletonLoaded}>
                  <ReactSelect
                    selectedOptionColorScheme='primary'
                    value={selectedOrgRole}
                    onChange={changeOrganizationAccess}
                    options={accessSelectOptions}
                    isDisabled={isUpdating || !hasRemoveAccess}
                    {...getOrganisationAccessReusedProps()}
                    aria-label='Organization access role'
                  />
                </Skeleton>
              </Box>
              <Box>
                <Button
                  variant='solid'
                  colorScheme='primary'
                  isLoading={isUpdating}
                  onClick={saveOrganizationAccess}
                  isDisabled={
                    saveButtonDisabled ||
                    selectedOrgRole?.value === currentOrgRole?.value
                  }
                  data-testid='save-org-access-button'
                >
                  Save
                </Button>
              </Box>
            </HStack>
          ) : (
            renderErrorAlert('organization-access-unavailable', {
              mt: '0',
              message: MEMBER_DETAILS_ERROR_MESSAGES.noData,
            })
          )}

          <Box mt='8'>
            <Heading size='sm' mb='3' fontWeight='semibold'>
              App access
            </Heading>
            {!isSkeletonLoaded || isUpdating ? (
              <Skeleton maxWidth='sm' height='10' />
            ) : orgRole?.name === ORG_ROLES.ORG_OWNER ? (
              <Text variant='subtle' data-testid='owner-access-wrapper'>
                You are granting ownership rights to this organization. <br />
                This includes access to all apps, the admin section and
                management of all owners.
              </Text>
            ) : orgRole?.name === ORG_ROLES.ORG_ADMIN ? (
              <Text variant='subtle' data-testid='admin-access-wrapper'>
                You are granting admin rights to this organization. <br />
                This includes access to all apps and the admin section.
              </Text>
            ) : allRoles.length > 0 ||
              (orgRole?.name === ORG_ROLES.ORG_USER && !isError) ? (
              <MemberDetailsProductAccess
                roles={filterRolesForOrganization(organizationGqlId, allRoles)}
                userId={userId}
              />
            ) : (
              renderErrorAlert('app-access-unavailable', {
                mt: '0',
                message: MEMBER_DETAILS_ERROR_MESSAGES.noData,
              })
            )}
          </Box>
        </Box>

        {isUserGroupsEnabled && (
          <Box flex='1' minW='0'>
            <MemberTeamsSection
              teams={teams}
              isLoading={teamsLoading}
              hasResolvedTeams={hasResolvedTeams}
              memberId={userId}
              memberName={memberName}
              onTeamRemoved={handleTeamRemoved}
              onAddToTeamsClick={handleAddToTeamsClick}
              showAddButton={canManageTeams}
            />
          </Box>
        )}
      </Flex>
      {/* Delete confirmation modal */}
      <MemberRemoveConfirmation
        isOpen={!!hasRemoveAccess && showRemoveModal}
        userId={userId}
        name={givenName || ''}
        onClose={() => setShowRemoveModal(false)}
        onCompleted={() => {
          setShowRemoveModal(false);
          setTimeout(() => {
            history.push(`/admin/members/page/1${history.location.search}`);
          }, 0);
        }}
      />
    </Box>
  );
};
